import { Check, Play, X } from 'lucide-react';
import { type KeyboardEvent, type ReactNode, useId, useState } from 'react';
import {
  Button as AriaButton,
  GridListItem,
  isFileDropItem,
  isTextDropItem
} from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import { Button, buttonVariants } from 'v5/design-system/Button';
import { Input } from 'v5/design-system/Input';
import { MediaBadge } from 'v5/design-system/MediaBadges';
import { Skeleton } from 'v5/design-system/Skeleton';
import { ImmersiveCard } from 'v5/patterns/collection/ImmersiveCard';
import {
  TileCollection,
  type TileSelection
} from 'v5/patterns/collection/TileCollection';
import {
  ImmersiveCardSkeleton,
  TileCollectionSkeleton
} from 'v5/patterns/collection/TileCollectionSkeleton';
import { gridLayout } from 'v5/patterns/collection/tileLayouts';
import {
  accepts,
  formatSize
} from 'v5/patterns/forms/fields/image-upload/MediaGallery';
import { MediaLightbox } from 'v5/patterns/forms/fields/image-upload/MediaLightbox';
import {
  PendingTile,
  uploadTileKey
} from 'v5/patterns/forms/fields/image-upload/PendingTile';
import { useClipboardFiles } from 'v5/patterns/forms/fields/image-upload/useClipboardFiles';
import { DragPicture } from 'v5/patterns/dnd/DragPicture';
import { MediaThumbnail } from 'v5/patterns/forms/fields/image-upload/MediaThumbnail';
import type { PendingUpload } from 'v5/patterns/forms/fields/image-upload/useMediaUploads';
import { cn } from 'v5/platform/dom/cn';
import type { WithServices } from 'v5/platform/ts/services';
import type { WithToastService } from 'v5/services/front/ToastService/ToastService';
import { SplitViewColumn } from 'v5/patterns/page-layout/SplitViewColumn';
import { SplitViewLayout } from 'v5/patterns/page-layout/SplitViewLayout';
import { ScrollShadowWrapper } from 'v5/primitives/ScrollShadowWrapper';

export type GalleryEditorItemKind = 'image' | 'video' | 'file' | 'link';

/** A picture, video, file or link the editor shows; `data` is the host's own. */
export interface GalleryEditorItem<T> {
  /** Stable across renders and unique in its list. */
  id: string;
  name: string;
  kind: GalleryEditorItemKind;
  /** The picture to show; for an image, the image itself. */
  thumbnail: string | null;
  /** What opens at full size. */
  src: string;
  /** Shown in the tile's corner, after the cover's mark. */
  badge?: ReactNode;
  data: T;
}

/** What a source item carries while it is dragged into the gallery: its id. */
const SOURCE_ITEM_TYPE = 'application/x-gallery-editor-source-item';

/**
 * The gallery being built, and beside it, while `panel` is given, a column of sources to add from
 * (`GalleryEditor.Panel`): the board's split view, so the column narrows the gallery and, where
 * there is no room for both, opens over it. The host owns the items, the reads and the save.
 */
function GalleryEditorRoot({
  panel,
  children
}: {
  panel: ReactNode;
  /** `GalleryEditor.Main`. */
  children: ReactNode;
}) {
  return (
    <SplitViewLayout
      flush
      className="min-h-0 flex-1"
      content={children}
      column={panel || undefined}
    />
  );
}

/**
 * The gallery's area: a heading with the count and the host's actions, the gallery, a hint
 * under it. Only the gallery scrolls.
 */
function Main({
  title,
  count,
  actions,
  hint,
  children
}: {
  title: string;
  count: number | null;
  actions?: ReactNode;
  hint: ReactNode;
  children: ReactNode;
}) {
  const headingId = useId();
  return (
    <section
      aria-labelledby={headingId}
      className="flex min-h-0 min-w-0 flex-1 flex-col gap-3 p-5"
    >
      <div className="flex shrink-0 items-center gap-3">
        <h3 id={headingId} className="text-base font-semibold">
          {title}
        </h3>
        {count !== null && (
          <span className="text-sm font-medium text-fg-muted">{count}</span>
        )}
        <span className="flex-1" />
        {actions}
      </div>
      <ScrollShadowWrapper
        edge="soft"
        className="-mx-4 -my-[4px]"
        // Room for the drop line before a row's first tile and the focus rings.
        scrollerClassName="px-4 py-[4px] with-styled-scrollbars"
      >
        {children}
      </ScrollShadowWrapper>
      <p className="shrink-0 text-sm text-fg-muted">{hint}</p>
    </section>
  );
}

/** The column to add from: a title, a close button, and what scrolls under them. */
function Panel({
  title,
  onClose,
  children
}: {
  title: string;
  onClose(): void;
  children: ReactNode;
}) {
  return (
    <SplitViewColumn onClose={onClose}>
      <SplitViewColumn.Header>
        <SplitViewColumn.Title>{title}</SplitViewColumn.Title>
        <SplitViewColumn.Close />
      </SplitViewColumn.Header>
      <SplitViewColumn.Body>
        <div className="flex min-h-full flex-col gap-4 p-4">{children}</div>
      </SplitViewColumn.Body>
    </SplitViewColumn>
  );
}

/** A dragged tile's layer: its picture drawn at once, or its kind and name when it has none. */
function dragLayerOf<T>(item: GalleryEditorItem<T>) {
  const picture =
    item.kind === 'image' ? (item.thumbnail ?? item.src) : item.thumbnail;
  return picture ? (
    <DragPicture src={picture} />
  ) : (
    <MediaThumbnail item={mediaItemOf(item)} />
  );
}

/** A gallery item as the media gallery and its thumbnails take it. */
function mediaItemOf<T>(item: GalleryEditorItem<T>) {
  return {
    name: item.name,
    mimeType: mimeTypeOfKind[item.kind],
    src: item.kind === 'image' ? (item.thumbnail ?? item.src) : item.src,
    poster: item.thumbnail ?? undefined
  };
}

export interface SourceGridProps<T> {
  label: string;
  items: GalleryEditorItem<T>[];
  /** Items the gallery already holds: shown pressed. */
  pickedIds: ReadonlySet<string>;
  onPick(item: GalleryEditorItem<T>): void;
  onUnpick(item: GalleryEditorItem<T>): void;
  /** Shown when there are no items. */
  emptyText: ReactNode;
  /** The host keeps which items are picked, for its actions on several at once. */
  selection?: Pick<TileSelection, 'selected' | 'onChange'>;
}

/**
 * Items to pick into the gallery, as the gallery's own tiles: a press or Enter adds one, a second
 * takes it out again. One not yet in the gallery also drags into it, to a place
 * (`GalleryEditor.Gallery`), with the mouse or from its handle. With `selection`, several are
 * picked as on every tile collection (the circle, Shift, the rectangle, ⌘A, Esc).
 */
function SourceGrid<T>({
  label,
  items,
  pickedIds,
  onPick,
  onUnpick,
  emptyText,
  selection
}: SourceGridProps<T>) {
  const { t } = useTranslation('patterns', { keyPrefix: 'GalleryEditor' });
  const { t: tTile } = useTranslation('patterns', {
    keyPrefix: 'ImageCollectionUploadField'
  });
  if (items.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-border-default px-6 py-10 text-center text-sm text-fg-muted">
        {emptyText}
      </p>
    );
  }
  return (
    <TileCollection
      label={label}
      items={items}
      keyOf={item => item.id}
      textOf={item => item.name}
      layout={gridLayout({})}
      selection={
        selection && {
          selected: new Set(
            [...selection.selected].filter(key =>
              items.some(item => item.id === key)
            )
          ),
          onChange: selection.onChange,
          marquee: true
        }
      }
      outgoing={{
        // One already in the gallery stays: no handle, no drag.
        itemOf: item =>
          pickedIds.has(item.id)
            ? null
            : { [SOURCE_ITEM_TYPE]: item.id, 'text/plain': item.name },
        dragLayer: dragLayerOf
      }}
      onOpen={key => {
        const item = items.find(candidate => candidate.id === key);
        if (!item) return;
        if (pickedIds.has(key)) onUnpick(item);
        else onPick(item);
      }}
      renderTile={(item, tile) => (
        <ImmersiveCard
          tile={tile}
          media={<MediaThumbnail item={mediaItemOf(item)} />}
          name={item.name}
          // while picking, a press picks: it adds nothing
          caption={
            tile.picking
              ? undefined
              : t(pickedIds.has(item.id) ? 'removeFromGallery' : 'addToGallery')
          }
          badge={
            pickedIds.has(item.id) ? (
              <span
                className={buttonVariants({
                  variant: 'overlay',
                  size: 'xs',
                  shape: 'rounded'
                })}
              >
                <Check />
                {t('added')}
              </span>
            ) : undefined
          }
          ratio={4 / 5}
          labels={{
            select: tTile('select', { name: item.name }),
            reorder: t('dragIntoGallery', { name: item.name }),
            remove: item.name
          }}
          data-testid="gallery-source-tile"
        />
      )}
    />
  );
}

export interface LinkFieldProps {
  label: string;
  /** Which links it takes, under the field. */
  description: ReactNode;
  value: string;
  onValueChange(value: string): void;
  /** What the field holds can be added now; Enter adds it too. */
  canAdd: boolean;
  onAdd(): void;
  /** The address holds nothing to add. */
  invalid: boolean;
}

/** An address to add from, with its Add button; what it holds shows elsewhere (`GalleryEditor.LinkPreview`). */
function LinkField({
  label,
  description,
  value,
  onValueChange,
  canAdd,
  onAdd,
  invalid
}: LinkFieldProps) {
  const { t } = useTranslation('patterns', { keyPrefix: 'GalleryEditor' });
  const id = useId();
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-base font-semibold text-fg-default">
        {label}
      </label>
      <div className="flex gap-2">
        <Input
          id={id}
          size="sm"
          className="flex-1"
          type="url"
          inputMode="url"
          placeholder="https://"
          value={value}
          aria-invalid={invalid || undefined}
          aria-describedby={`${id}-description`}
          onChange={event => onValueChange(event.target.value)}
          // Inside a dialog's form Enter would submit it; here it adds the link. ⌘↵ stays the
          // form's.
          onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key !== 'Enter' || event.metaKey || event.ctrlKey) return;
            event.preventDefault();
            if (canAdd) onAdd();
          }}
        />
        <Button
          type="button"
          variant="primaryAlt"
          size="sm"
          disabled={!canAdd}
          onClick={onAdd}
        >
          {t('addLink')}
        </Button>
      </div>
      <p id={`${id}-description`} className="text-sm text-fg-muted">
        {description}
      </p>
    </div>
  );
}

export type LinkPreviewProps = {
  /** Back to no link: clears the field. */
  onClear(): void;
} & (
  | {
      type: 'media';
      /** The picture, or the video's poster frame. */
      src: string;
      /** A video: the play mark over the picture. */
      playable: boolean;
      /** Set by the host from `onLoad`: until then a placeholder stands in. */
      loaded: boolean;
      onLoad(): void;
      onError(): void;
      /** Under the picture, e.g. the video's provider and title. */
      caption?: ReactNode;
    }
  | {
      type: 'problem';
      icon: ReactNode;
      title: string;
      description: ReactNode;
    }
);

/**
 * What a link holds, filling its box: the picture at its own proportions, a
 * placeholder while it loads, or why there is nothing to add.
 */
function LinkPreview(props: LinkPreviewProps) {
  const { t } = useTranslation('patterns', { keyPrefix: 'GalleryEditor' });
  return (
    <div
      aria-live="polite"
      className="relative flex size-full flex-col overflow-hidden rounded-lg border border-border-default bg-bg-default text-left"
      data-testid="link-preview"
    >
      {props.type === 'media' ? (
        <figure className="flex min-h-0 flex-1 flex-col">
          <div className="relative flex min-h-0 flex-1 items-center justify-center bg-bg-inset/40">
            {!props.loaded && (
              <Skeleton
                className="absolute inset-0 rounded-none"
                role="status"
                aria-label={t('loadingPreview')}
              />
            )}
            <img
              // A new address is a new picture: never the last one's load state.
              key={props.src}
              src={props.src}
              alt=""
              onLoad={props.onLoad}
              onError={props.onError}
              className={cn(
                'max-h-full w-auto max-w-full object-contain transition-opacity',
                props.loaded ? 'opacity-100' : 'opacity-0'
              )}
            />
            {props.playable && props.loaded && (
              <MediaBadge>
                <Play className="fill-current" />
              </MediaBadge>
            )}
          </div>
          {props.caption && (
            <figcaption className="flex min-w-0 shrink-0 items-center gap-2 border-t border-border-default px-3 py-2 text-sm">
              {props.caption}
            </figcaption>
          )}
        </figure>
      ) : (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 text-center">
          <span className="flex size-10 items-center justify-center rounded-full bg-bg-subtle text-fg-muted ring-1 ring-border-default">
            {props.icon}
          </span>
          <span className="text-base font-semibold text-fg-default">
            {props.title}
          </span>
          <span className="text-sm text-fg-muted">{props.description}</span>
        </div>
      )}
      <Button
        type="button"
        variant={props.type === 'media' ? 'overlay' : 'secondary'}
        size="xs"
        layout="icon"
        className="absolute top-1.5 right-1.5"
        aria-label={t('clearLink')}
        onClick={props.onClear}
      >
        <X />
      </Button>
    </div>
  );
}

/** A small link-like action in the gallery's heading row. */
function Action({
  children,
  onPress,
  isDisabled
}: {
  children: ReactNode;
  onPress(): void;
  isDisabled?: boolean;
}) {
  return (
    <AriaButton
      onPress={onPress}
      isDisabled={isDisabled}
      className="rounded-sm text-sm font-semibold text-brand-600 outline-none transition-colors hover:text-brand-800 hover:underline data-disabled:cursor-default data-disabled:text-fg-muted data-disabled:no-underline data-focus-visible:ring-2 data-focus-visible:ring-bg-emphasis/70"
    >
      {children}
    </AriaButton>
  );
}

export type GalleryProps<T> = WithServices<[WithToastService]> & {
  items: GalleryEditorItem<T>[];
  /** Files on their way in, shown after the items. */
  uploads: PendingUpload[];
  onReorder(ids: string[]): void;
  /** From a tile, the full-size view, or Delete on the picked ones. */
  onRemove(ids: string[]): void;
  onRetryUpload(upload: PendingUpload): void;
  onDismissUpload(id: string): void;
  /** In the tile after the items while there are none. */
  emptyText: ReactNode;
  /** 3 without it. */
  columns?: number;
  /** Files dropped on the gallery, at the place they land (`null`: the end); absent, it takes none. */
  files?: {
    /** File types it takes, as `<input accept>` lists them. */
    accept: string;
    /** Larger files are refused with a toast. */
    maxSize: number;
    onAdd(files: File[], at: number | null): void;
  };
  /** Items dragged in from a `GalleryEditor.SourceGrid`, by id, at the place they land. */
  onAddSourceItems?(ids: string[], at: number | null): void;
  /** The host keeps which items are picked, for its actions on several at once. */
  selection?: Pick<TileSelection, 'selected' | 'onChange'>;
};

const mimeTypeOfKind = {
  image: 'image/*',
  video: 'video/*',
  file: undefined,
  link: 'text/uri-list'
} satisfies Record<GalleryEditorItemKind, string | undefined>;

/** The tile after the items: where a picture dragged in lands, and what to do while there is none. */
const DROP_TILE = 'drop-tile';

/**
 * The gallery in its order, the first marked as the cover: drag or move from the keyboard to
 * reorder, remove from a tile, open one at full size. Pictures dragged in from the sources, or
 * files from the computer, land where they are dropped.
 */
function Gallery<T>({
  services,
  items,
  uploads,
  onReorder,
  onRemove,
  onRetryUpload,
  onDismissUpload,
  emptyText,
  columns = 3,
  files,
  onAddSourceItems,
  selection
}: GalleryProps<T>) {
  const { t } = useTranslation('patterns', { keyPrefix: 'GalleryEditor' });
  const { t: tTile } = useTranslation('patterns', {
    keyPrefix: 'ImageCollectionUploadField'
  });
  const [previewKey, setPreviewKey] = useState<string | null>(null);
  const [thumbnailsShown, setThumbnailsShown] = useState(false);
  const mediaItems = items.map(item => ({
    ...mediaItemOf(item),
    key: item.id
  }));

  const takeFiles = (dropped: File[], at: number | null) => {
    if (!files) return;
    const taken = dropped.filter(file => {
      if (!accepts(files.accept, file)) {
        services.toastService.open({
          type: 'error',
          message: tTile('rejected', { name: file.name })
        });
        return false;
      }
      if (file.size > files.maxSize) {
        services.toastService.open({
          type: 'error',
          message: tTile('tooLarge', {
            name: file.name,
            size: formatSize(files.maxSize)
          })
        });
        return false;
      }
      return true;
    });
    if (taken.length > 0) files.onAdd(taken, at);
  };
  const clipboard = useClipboardFiles({
    accepts: () => true,
    onFiles: pasted => takeFiles(pasted, null)
  });

  return (
    <TileCollection
      label={t('galleryLabel')}
      items={items}
      keyOf={item => item.id}
      textOf={item => item.name}
      layout={gridLayout({ columns })}
      selection={
        selection && {
          // After a drop React Aria picks what came in, here their uploads, and retries for as
          // long as it can't; an upload leaves the picks when it ends.
          selected: new Set(
            [...selection.selected].filter(
              key =>
                items.some(item => item.id === key) ||
                uploads.some(upload => uploadTileKey(upload.id) === key)
            )
          ),
          onChange: selection.onChange,
          marquee: true
        }
      }
      onRemove={keys =>
        onRemove(keys.filter(key => items.some(item => item.id === key)))
      }
      reorder={
        items.length > 1
          ? {
              onReorder,
              dragLayer: dragLayerOf
            }
          : undefined
      }
      incoming={
        files || onAddSourceItems
          ? {
              onDrop: async (dropped, at) => {
                const sourceIds = await Promise.all(
                  dropped
                    .filter(isTextDropItem)
                    .filter(item => item.types.has(SOURCE_ITEM_TYPE))
                    .map(item => item.getText(SOURCE_ITEM_TYPE))
                );
                if (sourceIds.length > 0) onAddSourceItems?.(sourceIds, at);
                const droppedFiles = await Promise.all(
                  dropped.filter(isFileDropItem).map(item => item.getFile())
                );
                if (droppedFiles.length > 0) takeFiles(droppedFiles, at);
              },
              onPaste: files ? clipboard.onPaste : undefined
            }
          : undefined
      }
      onOpen={setPreviewKey}
      renderTile={(item, tile) => {
        const index = items.indexOf(item);
        return (
          <ImmersiveCard
            tile={tile}
            media={<MediaThumbnail item={mediaItemOf(item)} />}
            name={item.name}
            badge={
              index === 0 ? (
                <span
                  className={buttonVariants({
                    variant: 'overlay',
                    size: 'xs',
                    shape: 'rounded'
                  })}
                >
                  {t('cover')}
                </span>
              ) : (
                item.badge
              )
            }
            emphasized={index === 0}
            onRemove={() => onRemove([item.id])}
            ratio={4 / 5}
            controlsAtRest
            labels={{
              select: tTile('select', { name: item.name }),
              reorder: tTile('reorder', { name: item.name }),
              remove: tTile('remove', { name: item.name })
            }}
            data-testid="gallery-tile"
          />
        );
      }}
      trailing={{
        keys: [...uploads.map(upload => uploadTileKey(upload.id)), DROP_TILE],
        unpickable: [DROP_TILE],
        render: dropLineAt => (
          <>
            {uploads.map((upload, index) => (
              <PendingTile
                key={upload.id}
                upload={upload}
                // the picture as its finished tile shows it
                preview={
                  upload.previewUrl ? (
                    <MediaThumbnail
                      item={{
                        name: upload.file.name,
                        mimeType: upload.file.type,
                        src: upload.previewUrl
                      }}
                    />
                  ) : undefined
                }
                dropLine={dropLineAt(index)}
                onRetry={() => onRetryUpload(upload)}
                onDismiss={() => onDismissUpload(upload.id)}
                portrait
              />
            ))}
            <GridListItem
              id={DROP_TILE}
              textValue={items.length === 0 ? String(emptyText) : t('dropHere')}
              className={cn(
                'relative min-h-0 min-w-0 outline-none',
                items.length === 0 && uploads.length === 0
                  ? 'col-span-full min-h-48'
                  : 'aspect-[4/5]'
              )}
            >
              <span className="absolute inset-0 flex items-center justify-center rounded-lg border-2 border-dashed border-border-default bg-bg-subtle p-3 text-center text-sm font-semibold text-fg-muted">
                {items.length === 0 ? emptyText : t('dropHere')}
              </span>
              {dropLineAt(uploads.length)}
            </GridListItem>
          </>
        )
      }}
      className="flex w-full flex-col"
      data-testid="gallery-editor-gallery"
    >
      <MediaLightbox
        items={mediaItems}
        currentKey={previewKey}
        onCurrentKeyChange={setPreviewKey}
        onRemove={item => onRemove([item.key])}
        removing={new Set()}
        thumbnailsShown={thumbnailsShown}
        onThumbnailsShownChange={setThumbnailsShown}
      />
    </TileCollection>
  );
}

/** `Gallery` while it loads: placeholders where its tiles will stand, `count` when it is known. */
function GallerySkeleton({
  columns = 3,
  count
}: Pick<GalleryProps<unknown>, 'columns'> & { count?: number }) {
  const { t } = useTranslation('patterns', { keyPrefix: 'GalleryEditor' });
  return (
    <TileCollectionSkeleton
      label={t('loadingGallery')}
      layout={gridLayout({ columns })}
      count={count}
      className="flex w-full flex-col"
      renderTile={tile => <ImmersiveCardSkeleton tile={tile} ratio={4 / 5} />}
    />
  );
}

/** `SourceGrid` while it loads, in its layout. */
function SourceGridSkeleton({
  label,
  count
}: {
  /** What loads ("Loading pins"). */
  label: string;
  count?: number;
}) {
  return (
    <TileCollectionSkeleton
      label={label}
      layout={gridLayout({})}
      count={count}
      renderTile={tile => <ImmersiveCardSkeleton tile={tile} ratio={4 / 5} />}
    />
  );
}

/**
 * Edits a gallery: the gallery in its order with the cover first, and a panel of sources beside
 * it (picks, uploads, links). Compose it from the parts; it holds no items, reads nothing and
 * saves nothing.
 */
export const GalleryEditor = Object.assign(GalleryEditorRoot, {
  Main,
  Panel,
  Action,
  SourceGrid,
  SourceGridSkeleton,
  LinkField,
  LinkPreview,
  Gallery,
  GallerySkeleton
});
